import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-5 text-left",
  "data-testid": "bulk-sector-specialty-fields"
}
const _hoisted_2 = {
  key: 0,
  class: "rounded bg-gray-50 px-3 py-2 text-sm text-gray-600",
  "data-testid": "bulk-disabled-reason"
}
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_5 = { class: "font-bold text-gray-800" }
const _hoisted_6 = ["data-testid"]
const _hoisted_7 = ["aria-label"]
const _hoisted_8 = ["aria-checked", "disabled", "data-testid", "onClick"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_10 = { class: "text-gray-500" }
const _hoisted_11 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_12 = ["data-testid"]

import { computed, watch } from 'vue'
import SectorCombobox from './SectorCombobox.vue'
import SpecialtyCombobox from './SpecialtyCombobox.vue'
import {
  currentValueSummary,
  describeFieldPreview,
  previewSector,
  previewSpecialty,
  type BulkFieldState,
  type BulkItem
} from '@/modules/company/Dashboard/lenses/bulkPreview'
import type { BulkFieldMode } from '@/Network/Types/Requests/StationRequests'
import type { StationSectorRef } from '@/Network/Types/Responses/StationResponses'
import type { SpecialtySelection } from '@/utils/sectorSpecialty'
import { useIsMobile } from '@/hooks/useIsMobile'

/**
 * Bloco "Setor e especialidade" de um lote (H7/H8-b): Manter / Definir / Limpar por campo,
 * com a prévia local. Controlado (`v-model:sector` / `v-model:specialty`); nunca sugere
 * valor (P2) — "Definir" começa vazio até o usuário escolher.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'BulkSectorSpecialtyFields',
  props: {
    items: {},
    sector: {},
    specialty: {},
    sectorLockedReason: { default: null },
    disabledReason: { default: null }
  },
  emits: ["update:sector", "update:specialty"],
  setup(__props: any, { emit }) {

const props = __props;





// Celular (QA-01): o reset do Vuetify zera `border-style` em <button>; o desktop fica como está.
const { isMobileLayout } = useIsMobile()

const unitIds = computed(() => [
  ...new Set(props.items.map((item) => item.healthUnitId).filter((id): id is number => id !== null))
])
const companyIds = computed(() => [
  ...new Set(props.items.map((item) => item.companyId).filter((id): id is number => id !== null))
])

watch(
  () => props.sectorLockedReason,
  (reason) => {
    if (reason && props.sector.mode === 'set') emit('update:sector', { mode: 'keep', value: null })
  },
  { immediate: true }
)

const fields = computed(() => [
  {
    key: 'sector' as const,
    title: 'Setor',
    state: props.sector,
    current: currentValueSummary(props.items, 'setor'),
    preview: describeFieldPreview(
      previewSector(props.items, props.sector),
      props.sector.mode,
      !!props.sector.value,
      'setor'
    ),
    setMode: (mode: BulkFieldMode) =>
      emit('update:sector', { mode, value: mode === 'set' ? props.sector.value : null }),
    options: [
      {
        mode: 'keep' as const,
        label: 'Manter como está',
        hint: 'Nenhum plantão muda',
        disabled: false
      },
      {
        mode: 'set' as const,
        label: 'Definir valor',
        hint: props.sectorLockedReason ?? 'Mesmo valor em todos',
        disabled: !!props.sectorLockedReason
      },
      { mode: 'clear' as const, label: 'Limpar', hint: 'Deixa o campo vazio', disabled: false }
    ]
  },
  {
    key: 'specialty' as const,
    title: 'Especialidade',
    state: props.specialty,
    current: currentValueSummary(props.items, 'especialidade'),
    preview: describeFieldPreview(
      previewSpecialty(props.items, props.specialty),
      props.specialty.mode,
      !!props.specialty.value,
      'especialidade'
    ),
    setMode: (mode: BulkFieldMode) =>
      emit('update:specialty', { mode, value: mode === 'set' ? props.specialty.value : null }),
    options: [
      {
        mode: 'keep' as const,
        label: 'Manter como está',
        hint: 'Nenhum plantão muda',
        disabled: false
      },
      {
        mode: 'set' as const,
        label: 'Definir valor',
        hint:
          companyIds.value.length > 1
            ? 'Só do catálogo oficial (várias empresas)'
            : 'Mesmo valor em todos',
        disabled: false
      },
      { mode: 'clear' as const, label: 'Limpar', hint: 'Deixa o campo vazio', disabled: false }
    ]
  }
])

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.disabledReason)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_ctx.disabledReason), 1))
      : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(fields.value, (field) => {
          return (_openBlock(), _createElementBlock("section", {
            key: field.key,
            "data-testid": `bulk-${field.key}-field`
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("span", _hoisted_5, _toDisplayString(field.title), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.current, (chip) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: chip,
                  class: "rounded-full bg-gray-100 px-2 text-xs text-gray-600",
                  "data-testid": `bulk-${field.key}-current`
                }, _toDisplayString(chip), 9, _hoisted_6))
              }), 128))
            ]),
            _createElementVNode("div", {
              class: "mt-2 grid grid-cols-1 gap-2 md:grid-cols-3",
              role: "radiogroup",
              "aria-label": field.title
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.options, (option) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: option.mode,
                  type: "button",
                  role: "radio",
                  "aria-checked": field.state.mode === option.mode,
                  class: _normalizeClass(["rounded border px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50", [
              field.state.mode === option.mode ? 'border-primary30 bg-primary5' : 'border-gray-300',
              { 'border-solid': _unref(isMobileLayout) }
            ]]),
                  disabled: option.disabled,
                  "data-testid": `bulk-${field.key}-mode-${option.mode}`,
                  onClick: ($event: any) => (field.setMode(option.mode))
                }, [
                  _createElementVNode("b", null, _toDisplayString(option.label), 1),
                  _hoisted_9,
                  _createElementVNode("small", _hoisted_10, _toDisplayString(option.hint), 1)
                ], 10, _hoisted_8))
              }), 128))
            ], 8, _hoisted_7),
            (field.state.mode === 'set')
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  (field.key === 'sector')
                    ? (_openBlock(), _createBlock(SectorCombobox, {
                        key: 0,
                        "model-value": _ctx.sector.value,
                        "health-unit-id": unitIds.value.length === 1 ? unitIds.value[0] : null,
                        "test-id": "bulk-sector-combobox",
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (emit('update:sector', { mode: 'set', value: $event })))
                      }, null, 8, ["model-value", "health-unit-id"]))
                    : (_openBlock(), _createBlock(SpecialtyCombobox, {
                        key: 1,
                        "model-value": _ctx.specialty.value,
                        "company-id": companyIds.value.length === 1 ? companyIds.value[0] : null,
                        "official-only": companyIds.value.length !== 1,
                        "test-id": "bulk-specialty-combobox",
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (emit('update:specialty', { mode: 'set', value: $event })))
                      }, null, 8, ["model-value", "company-id", "official-only"]))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("p", {
              class: "mt-2 text-sm text-gray-700",
              "data-testid": `bulk-${field.key}-preview`
            }, _toDisplayString(field.preview), 9, _hoisted_12)
          ], 8, _hoisted_3))
        }), 128))
  ]))
}
}

})